import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { portalApi } from "../api/portalApi";

// Async thunk to collect NP jobs data based on criteria
export const collectNPJobs = createAsyncThunk(
  "npJobs/collectData",
  async ({ jobId }, { dispatch, rejectWithValue }) => {
    try {
      const npJobsData = {
        csm: [],
        fertigations: [],
        bmx: [],
        inducerTubes: [],
        rockets: [],
        suctionLines: []
      };

      // Use RTK Query to fetch all required tables in parallel
      const [
        stationEnclosureResult,
        fertigationResult,
        generalProjectResult,
        pumpManifoldResult,
        hcRequirementsResult,
      ] = await Promise.all([
        dispatch(portalApi.endpoints.getStationEnclosure.initiate(jobId, { forceRefetch: true })),
        dispatch(portalApi.endpoints.getFertigation.initiate(jobId, { forceRefetch: true })),
        dispatch(portalApi.endpoints.getGeneralProjectInfo.initiate(jobId, { forceRefetch: true })),
        dispatch(portalApi.endpoints.getPumpManifoldPiping.initiate(jobId, { forceRefetch: true })),
        dispatch(portalApi.endpoints.getHcRequirements.initiate(jobId, { forceRefetch: true })),
      ]);

      const tableData = {
        stationEnclosure: stationEnclosureResult.data
          ? (Array.isArray(stationEnclosureResult.data) ? stationEnclosureResult.data : [stationEnclosureResult.data])
          : [],
        fertigation: fertigationResult.data
          ? (Array.isArray(fertigationResult.data) ? fertigationResult.data : [fertigationResult.data])
          : [],
        generalProject: generalProjectResult.data
          ? (Array.isArray(generalProjectResult.data) ? generalProjectResult.data : [generalProjectResult.data])
          : [],
        pumpManifoldPiping: pumpManifoldResult.data
          ? (Array.isArray(pumpManifoldResult.data) ? pumpManifoldResult.data : [pumpManifoldResult.data])
          : [],
        hcRequirements: hcRequirementsResult.data
          ? (Array.isArray(hcRequirementsResult.data) ? hcRequirementsResult.data : [hcRequirementsResult.data])
          : [],
      };

      // CSM - StationEnclosure Table: EnclosureType = CSM & HasEnclosure = true
      npJobsData.csm = tableData.stationEnclosure.filter(item =>
        item.EnclosureType === 'CSM' && item.HasEnclosure === true
      );

      // Fertigations - Fertigation Table: HasFertigation = true & WatertronicsFertigation StartsWith "EZ"
      npJobsData.fertigations = tableData.fertigation.filter(item =>
        item.HasFertigation === true &&
        item.WatertronicsFertigation &&
        item.WatertronicsFertigation.startsWith('EZ')
      );

      // BMX - GeneralProject Table: ModelNumber StartsWith BMX
      npJobsData.bmx = tableData.generalProject.filter(item =>
        item.ModelNumber && item.ModelNumber.startsWith('BMX')
      );

      // Inducer Tube - PumpManifoldPiping Table
      npJobsData.inducerTubes = tableData.pumpManifoldPiping.filter(item =>
        item.HasPiping === true &&
        item.HDPEAccessoryForSubPump === 'Horizontal Inducer Tube'
      );

      // Rocket - PumpManifoldPiping Table
      npJobsData.rockets = tableData.pumpManifoldPiping.filter(item =>
        item.HasPiping === true &&
        item.HDPEAccessoryForSubPump === 'Vertical Inducer Tube'
      );

      // Suction Lines - HCRequirements Table
      npJobsData.suctionLines = tableData.hcRequirements.filter(item =>
        item.SuctionLineMaterial === 'HDPE' &&
        item.SuctionLinesBy === 'Watertronics'
      );

      const metadata = {
        jobId,
        totalItems: Object.values(npJobsData).reduce((sum, category) => sum + category.length, 0),
        categoryCounts: {
          csm: npJobsData.csm.length,
          fertigations: npJobsData.fertigations.length,
          bmx: npJobsData.bmx.length,
          inducerTubes: npJobsData.inducerTubes.length,
          rockets: npJobsData.rockets.length,
          suctionLines: npJobsData.suctionLines.length
        },
        collectedAt: new Date().toISOString()
      };

      return { ...npJobsData, metadata };
    } catch (error) {
      console.error("Error collecting NP jobs data:", error);
      return rejectWithValue(error.message || "Failed to collect NP jobs data");
    }
  }
);

// NP Jobs slice
const npJobsSlice = createSlice({
  name: "npJobs",
  initialState: {
    data: {
      csm: [],
      fertigations: [],
      bmx: [],
      inducerTubes: [],
      rockets: [],
      suctionLines: [],
      metadata: null
    },
    loading: false,
    error: null,
    lastUpdated: null
  },
  reducers: {
    clearNPJobsData: (state) => {
      state.data = {
        csm: [],
        fertigations: [],
        bmx: [],
        inducerTubes: [],
        rockets: [],
        suctionLines: [],
        metadata: null
      };
      state.error = null;
      state.lastUpdated = null;
    },
    setNPJobsError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(collectNPJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(collectNPJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(collectNPJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Export actions
export const { clearNPJobsData, setNPJobsError } = npJobsSlice.actions;

// Selectors
export const selectNPJobs = (state) => state.npJobs.data;
export const selectNPJobsLoading = (state) => state.npJobs.loading;
export const selectNPJobsError = (state) => state.npJobs.error;
export const selectNPJobsMetadata = (state) => state.npJobs.data.metadata;

export default npJobsSlice.reducer;